// @owners { team: patients-team }
import { useEffect, useRef } from 'react';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { type CheckoutMailRequestState, useCheckoutMailRequest } from './useCheckoutMailRequest';

/**
 * Same mail-request state as {@link useCheckoutMailRequest}, plus `CHECKOUT__MAIL_REQUEST_ELIGIBILITY` analytics
 * (at most once per distinct payload). Prefer {@link useCheckoutMailRequest} when analytics are not needed.
 */
export const useCheckoutTrackMailRequest = (): CheckoutMailRequestState => {
  const { trackEvent } = useAnalytics();
  const mailRequest = useCheckoutMailRequest();
  const {
    mailRequestRequired,
    mailRequestRequiredMessage,
    hasMailRequestConfirmedError,
    showMailRequestConfirmedError,
    addressID,
    prescriptionIDs,
    deliveryMethod,
    shipmentID,
    isOnCompoundCheckoutPage,
    isLastShipment,
  } = mailRequest;

  const lastMailRequestAnalyticsKey = useRef<string | null>(null);

  useEffect(() => {
    const analyticsPayloadKey = JSON.stringify({
      mailRequestRequired,
      hasMailRequestConfirmedError,
      showMailRequestConfirmedError,
      addressID,
      prescriptionIDs: [...prescriptionIDs].sort((a, b) => a - b),
      deliveryMethod,
      shipmentID,
      isOnCompoundCheckoutPage,
      isLastShipment,
    });

    if (lastMailRequestAnalyticsKey.current === analyticsPayloadKey) {
      return;
    }
    lastMailRequestAnalyticsKey.current = analyticsPayloadKey;

    trackEvent({
      event: EVENTS.CHECKOUT__MAIL_REQUEST_ELIGIBILITY,
      params: {
        mailRequestRequired,
        hasMailRequestConfirmedError,
        showMailRequestConfirmedError,
        addressID,
        prescriptionIDs,
        deliveryMethod,
        shipmentID,
        isOnCompoundCheckoutPage,
        isLastShipment,
      },
    });
  }, [
    mailRequestRequired,
    hasMailRequestConfirmedError,
    showMailRequestConfirmedError,
    addressID,
    prescriptionIDs,
    deliveryMethod,
    shipmentID,
    isOnCompoundCheckoutPage,
    isLastShipment,
    trackEvent,
  ]);

  return {
    mailRequestRequired,
    mailRequestRequiredMessage,
    hasMailRequestConfirmedError,
    showMailRequestConfirmedError,
  };
};
